import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { catchError, distinctUntilChanged, map, of, startWith, Subject, Subscription, switchMap, take, takeUntil, timer } from 'rxjs';
import { isClient } from 'src/typingCheck';
import { Client } from 'src/typings';
import { ClientService } from '../client.service';
import { CLIENT_DIALOG_CONFIG, CreateComponent } from '../create/create.component';
import { formatClientPhoneCompact, getClientPrimaryPhone } from '../client-phone.util';

@Component({
  selector: 'app-client-selector',
  templateUrl: './client-selector.component.html',
  styleUrls: ['./client-selector.component.scss']
})
export class ClientSelectorComponent implements OnInit, OnDestroy {
  constructor(public dialog: MatDialog, private clientService: ClientService) {}

  clients: Client[] = [];

  value: any;

  loading = false;
  searchFailed = false;

  @Input() control: FormControl<string | Client | null> = new FormControl<string | Client>('');

  @Input() allowClear = false;

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger?: MatAutocompleteTrigger;

  valueSubscription: Subscription | undefined;
  private searchSubscription?: Subscription;
  private searchRequests = new Subject<{ query: string | null; immediate: boolean }>();
  private pendingEnterQuery: string | null = null;
  private createDialog?: MatDialogRef<CreateComponent>;
  private destroyed = false;

  public displayProperty(client: any) {
    return typeof client === 'string' ? client : client?.name || '';
  }

  ngOnInit(): void {
    this.searchSubscription = this.searchRequests.pipe(
      switchMap(({ query, immediate }) => timer(immediate ? 0 : 200).pipe(
        switchMap(() => this.clientService.search({ search_string: query, limit: 10 })),
        map((list) => ({ query, clients: list.data, failed: false })),
        catchError((error) => {
          console.error(error);
          return of({ query, clients: [] as Client[], failed: true });
        })
      ))
    ).subscribe(({ query, clients, failed }) => {
      this.clients = clients;
      this.loading = false;
      this.searchFailed = failed;
      if (this.pendingEnterQuery === query && typeof this.control.value === 'string' &&
        this.control.value === query) {
        this.pendingEnterQuery = null;
        if (!failed) this.selectOrCreateClient();
      }
    });
    this.valueSubscription = this.control.valueChanges.pipe(
      startWith(this.control.value), distinctUntilChanged()
    ).subscribe(() => this.search());
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.cancelPendingEnter();
    this.valueSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.createDialog?.close();
  }

  search(immediate = false) {
    const query = isClient(this.control.value) ? getClientPrimaryPhone(this.control.value) : this.control.value;
    if (query !== this.pendingEnterQuery) this.cancelPendingEnter();
    this.loading = true;
    this.searchFailed = false;
    this.clients = [];
    this.searchRequests.next({ query, immediate });
  }

  get hasClientQuery() {
    return typeof this.control.value === 'string' && !!this.control.value.trim();
  }

  get canCreateClient() {
    return !this.loading && !this.searchFailed && !this.clients.length && this.hasClientQuery;
  }

  onInputKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' || event.key === 'Tab') this.cancelPendingEnter();
    if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing ||
      event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.repeat || this.destroyed || typeof this.control.value !== 'string' ||
      !this.control.value.trim()) return;
    if (this.loading || this.searchFailed) {
      this.pendingEnterQuery = this.control.value;
      this.search(true);
      return;
    }
    this.selectOrCreateClient();
  }

  cancelPendingEnter() {
    this.pendingEnterQuery = null;
  }

  private selectOrCreateClient() {
    this.autocompleteTrigger?.closePanel();
    if (this.clients.length) this.control.setValue(this.clients[0]);
    else this.openDialog();
  }

  openDialog() {
    if (this.destroyed || this.createDialog || this.loading || this.searchFailed) return;
    this.cancelPendingEnter();
    const dialogRef = this.dialog.open(CreateComponent, {
      ...CLIENT_DIALOG_CONFIG,
      autoFocus: '.dialogActions button[type="submit"]',
      delayFocusTrap: false
    });
    this.createDialog = dialogRef;
    dialogRef.componentInstance.control = this.control;
    dialogRef.componentInstance.createClient.pipe(take(1), takeUntil(dialogRef.afterClosed())).subscribe((newClient) => {
      this.control.setValue(newClient);
      dialogRef.close();
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (this.createDialog === dialogRef) this.createDialog = undefined;
    });
  }

  handleSelect(event: MatAutocompleteSelectedEvent) {
    // Let autocomplete restore its input focus before the dialog takes focus.
    if (typeof event.option.value === 'string') Promise.resolve().then(() => this.openDialog());
  }

  getPhoneSummary(client: Client) {
    return formatClientPhoneCompact(client);
  }
}
